import { useEffect, useMemo } from "react";
import { UseTabManagementProps } from "./types";
import { useTabsState } from "./use-tabs-state";
import { useTabApiSync } from "./use-tab-api-sync";
import { useTabGroupDnD } from "./use-tab-group-dnd";
import { useTabDeletion } from "./use-tab-deletion";
import { useTabBulkSave } from "./use-tab-bulk-save";
import { useGroupCreation } from "./use-group-creation";
import { useGetDashboardByIdQuery } from "@/store/api/dashboard-api";
import { generateTabCopyName } from "@/utils/dashboard-naming-utils";
import { buildNavItems, renameGroup, validateGroupName } from "./group-utils";

export const useTabManagement = (props: UseTabManagementProps) => {
  // Subscribe to live dashboard data from RTK Query cache
  const { data: dashboardData, isFetching } = useGetDashboardByIdQuery(
    props.dashboardId || "",
    {
      skip: !props.dashboardId,
    },
  );

  // Convert dashboard tabs to TabData format - memoized to prevent infinite loops
  const liveTabs = useMemo(() => {
    if (!dashboardData?.tabs) return props.tabs;

    return [...dashboardData.tabs]
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .map((tab) => ({
        id: tab.id,
        label: tab.name,
        groupName: tab.groupName ?? null,
      }));
  }, [dashboardData?.tabs, props.tabs]);

  const { state, refs, actions } = useTabsState({ ...props, tabs: liveTabs });
  const { saveStructure, saveError, dismissSaveError } = useTabBulkSave(
    props.dashboardId,
    props.onSave,
  );
  const api = useTabApiSync(state, actions, props, saveStructure);
  const dnd = useTabGroupDnD(state, actions, saveStructure);
  const deletion = useTabDeletion(state, actions, api, saveStructure);
  const groupCreation = useGroupCreation(state.localTabs, actions.setLocalTabs, saveStructure);

  // auto-open add mode
  useEffect(() => {
    if (props.shouldStartInAddMode) actions.startAdd();
  }, [props.shouldStartInAddMode, actions]);

  // focus when adding
  useEffect(() => {
    if (state.isAddingNewTab) refs.newTabInputRef.current?.focus();
  }, [state.isAddingNewTab, refs.newTabInputRef]);

  const navItems = useMemo(() => buildNavItems(state.localTabs), [state.localTabs]);

  const handleRenameGroup = (oldName: string, newName: string): string | null => {
    if (newName.trim() === oldName) return null;
    const error = validateGroupName(newName, state.localTabs, oldName);
    if (error) return error;

    const before = state.localTabs;
    const next = renameGroup(before, oldName, newName);
    actions.setLocalTabs(next);
    void saveStructure(next, before);
    return null;
  };

  // public API — ergonomic and stable
  return {
    // State
    localTabs: state.localTabs,
    navItems,
    newTabName: state.newTabName,
    setNewTabName: actions.setNewTabName,
    newTabError: state.newTabError,
    tabErrors: state.tabErrors,
    dragged: state.dragged,
    dragOverTarget: state.dragOver,
    isAddingNewTab: state.isAddingNewTab,
    hoveredTabId: state.hoveredTabId,
    hoveredNewItem: state.hoveredNewItem,
    deleteConfirmation: state.deleteConfirmation,
    groupDeleteConfirmation: state.groupDeleteConfirmation,
    isLoadingTabs: isFetching,
    canAddGroup: state.localTabs.length >= 2,
    saveError,
    dismissSaveError,
    groupCreation,

    // Refs
    newTabInputRef: refs.newTabInputRef,

    // Handlers
    handleUpdateTabName: actions.updateLabel,
    handleTabNameBlur: (id: string, label: string) => {
      const original = props.tabs.find((t) => t.id === id)?.label;
      actions.validateOnBlur(id, label, original);
    },
    handleAddTab: actions.startAdd,
    handleSaveNewTab: () => {
      const created = actions.addOptimistic(state.newTabName);
      if (!created) return; // validation failed already sets error
      props.onAddTab(created.label); // server creation; temporary tab remains until props syncs back
    },
    handleCancelNewTab: actions.cancelAdd,

    handleDeleteTabClick: deletion.handleDeleteClick,
    confirmDeleteTab: deletion.confirmDelete,
    cancelDeleteTab: deletion.cancelDelete,

    handleDeleteGroupClick: deletion.handleDeleteGroupClick,
    confirmDeleteGroup: deletion.confirmDeleteGroup,
    cancelDeleteGroup: deletion.cancelDeleteGroup,
    handleRenameGroup,

    handleDuplicateTab: (tabId: string, currentName: string) => {
      const existingNames = state.localTabs.map((tab) => tab.label);
      const newName = generateTabCopyName(currentName, existingNames);
      props.onDuplicateTab?.(tabId, newName);
    },

    handleDragStart: dnd.handleDragStart,
    handleDragEnd: dnd.handleDragEnd,
    handleDragOver: dnd.handleDragOver,
    handleDragEnter: dnd.handleDragEnter,
    handleDragLeave: dnd.handleDragLeave,
    handleDrop: dnd.handleDrop,

    handleMouseEnter: (tabId: string) => actions.hoverTab(tabId),
    handleMouseLeave: () => actions.hoverTab(null),
    handleNewItemMouseEnter: () => actions.hoverNewItem(true),
    handleNewItemMouseLeave: () => actions.hoverNewItem(false),
  } as const;
};
